import React, { useState } from "react";
import { flagUrl } from "../../shared/flags";
import { RankBadge, type BadgeLook } from "../lobby/start/icons";
import s from "./PhonePlayerSheet.module.scss";

// /debug/phone-player: sketches of two landscape-phone pages, each in an 844 x
// 390 frame with made-up data.
//   Player settings (full screen, not a card): name, colour, the four things you
//   wear, a live worm wearing them, and the way to your Profile.
//   Profile: ranks and the Royale first, a way back to Player settings, and
//   three ways of moving between Overview, Achievements and Matches.
// The worm here is a drawn stand-in that drives a loop; the real page would use
// the game's own renderer.
const COLORS = ["#03a9f4", "#e91e63", "#4caf50", "#9c27b0", "#ff9800", "#cddc39", "#3f51b5"];
const ME = { name: "LuckyLuke", flag: "se" };
const SLOTS: [string, string][] = [["Flag", "flag"], ["Head", "crown"], ["Arena", ""], ["Victory", ""]];
const RANKS: { mode: string; tier: string; rating: number; pct: number; look: BadgeLook }[] = [
  { mode: "1 vs 1", tier: "Gold II", rating: 1171, pct: 71, look: { kind: "ranked", color: "#ffd24a", pct: 71, pips: 2, value: 1171 } },
  { mode: "6 FFA", tier: "Platinum I", rating: 1432, pct: 38, look: { kind: "ranked", color: "#3fe0c5", pct: 38, pips: 1, value: 1432 } },
];
const REWARDS = [[1, 10, "opened"], [5, 25, "ready"], [25, 50, "locked"], [75, 75, "locked"], [200, 100, "locked"]] as const;
const MATCHES = [["Royale #46", "3rd of 30", "2 h ago"], ["1 vs 1", "Won 3 to 1 · +18", "Yesterday"], ["6 FFA", "2nd of 6 · +9", "Yesterday"], ["Play Now", "1st of 8", "2 days ago"]];
const ACHS = [["First blood", 3, 5], ["Marathon", 62, 100], ["Untouchable", 1, 3], ["Royalty", 1, 5], ["Collector", 4, 10], ["Sharpshooter", 12, 25]] as const;

const I = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
const Back = () => <svg {...I} strokeWidth={2.4}><path d="M15 5l-7 7 7 7" /></svg>;
const Person = () => <svg {...I}><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7" /></svg>;
const Pencil = () => <svg {...I}><path d="M4 20l1-4L16 5l3 3L8 19l-4 1z" /></svg>;
const Crown = () => <svg {...I}><path d="M4 8l4 3.5L12 5l4 6.5L20 8l-1.6 10H5.6L4 8z" fill="currentColor" fillOpacity="0.9" /></svg>;
const Chev = () => <svg {...I} strokeWidth={2.4}><path d="M9 5l7 7-7 7" /></svg>;

// The slim top bar every full-screen phone page has: back, title, what the page
// adds, and the egg pill (your Golden Eggs, the way to the shop).
function Bar({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className={s.bar}>
      <span className={s.back}><Back /></span><b className={s.title}>{title}</b>
      <div className={s.barMid}>{children}</div>
      <span className={s.eggs}><i /><b>340</b><em>+</em></span>
    </div>
  );
}
// A worm driving a loop in the player's colour (see the note at the top).
function Worm({ color, crown = true, id }: { color: string; crown?: boolean; id: string }) {
  const d = "M40 70 C 40 20, 120 20, 150 60 S 250 110, 270 60 S 200 10, 150 60 S 60 120, 40 70";
  return (
    <svg className={s.worm} viewBox="0 0 310 130" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <path id={id} d={d} fill="none" />
      <path d={d} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round" pathLength={100} strokeDasharray="26 74">
        <animate attributeName="stroke-dashoffset" from="26" to="-74" dur="7s" repeatCount="indefinite" />
      </path>
      <g>
        <circle r="8" fill="#ffb74d" />
        {crown && <path d="M-7 -9l3.5 3 3.5 -5 3.5 5 3.5 -3 -1.4 6h-11.2z" fill="#ffd23f" stroke="#2a1c00" strokeWidth="1" />}
        <animateMotion dur="7s" repeatCount="indefinite"><mpath href={`#${id}`} /></animateMotion>
      </g>
    </svg>
  );
}
const Slot = ({ label, has, big }: { label: string; has: string; big?: boolean }) => (
  <span className={`${s.slot} ${has ? "" : s.slotEmpty} ${big ? s.slotBig : ""}`}>
    <i>{has === "flag" ? <img src={flagUrl(ME.flag)} alt="" /> : has === "crown" ? <Crown /> : "+"}</i><small>{label}</small>
  </span>
);
const Swatches = ({ pick, setPick, col }: { pick: number; setPick: (n: number) => void; col?: boolean }) => (
  <span className={`${s.sw} ${col ? s.swCol : ""}`}>{COLORS.map((c, i) => <button type="button" key={c} className={i === pick ? s.swOn : undefined} style={{ background: c }} onClick={() => setPick(i)} aria-label="Colour" />)}</span>
);
const ProfileLink = ({ wide }: { wide?: boolean }) => <span className={`${s.link} ${wide ? s.linkWide : ""}`}><Person />Profile<Chev /></span>;
const Frame = ({ children, tall }: { children: React.ReactNode; tall?: boolean }) => <div className={`${s.frame} ${tall ? s.frameTall : ""}`}>{children}{tall && <span className={s.fold}>the screen ends here</span>}</div>;

// ---------------- Player settings ----------------
// P1. The worm in the middle: name and colour left, what you wear right.
function P1() { const [c, setC] = useState(0); return (
  <Frame><Bar title="Player"><ProfileLink /></Bar>
    <div className={s.p1}>
      <div className={s.colL}><label>Name</label><span className={s.input}>{ME.name}</span><label>Colour</label><Swatches pick={c} setPick={setC} /><span className={s.out}>Sign out</span></div>
      <div className={s.stage}><Worm color={COLORS[c]} id="w1" /></div>
      <div className={s.colR}><label>Wearing</label><div className={s.grid2}>{SLOTS.map(([l, h]) => <Slot key={l} label={l} has={h} />)}</div></div>
    </div>
  </Frame>
); }
// P2. The worm is the page: the controls sit over its arena.
function P2() { const [c, setC] = useState(0); return (
  <Frame><div className={s.p2Stage}><Worm color={COLORS[c]} id="w2" /></div>
    <Bar title="Player"><ProfileLink /></Bar>
    <div className={s.p2}>
      <div className={s.p2Card}><span className={s.input}>{ME.name}</span><Swatches pick={c} setPick={setC} /></div>
      <div className={s.p2Slots}>{SLOTS.map(([l, h]) => <Slot key={l} label={l} has={h} />)}</div>
    </div>
  </Frame>
); }
// P3. Two halves: the worm with your name under it, the choices beside it.
function P3() { const [c, setC] = useState(0); return (
  <Frame><Bar title="Player" />
    <div className={s.p3}>
      <div className={s.p3Left}><div className={s.stage}><Worm color={COLORS[c]} id="w3" /></div><span className={s.nameLine}><img src={flagUrl(ME.flag)} alt="" /><b>{ME.name}</b><Pencil /></span></div>
      <div className={s.p3Right}>
        <label>Colour</label><Swatches pick={c} setPick={setC} />
        <label>Wearing</label><div className={s.row4}>{SLOTS.map(([l, h]) => <Slot key={l} label={l} has={h} />)}</div>
        <div className={s.p3Foot}><ProfileLink wide /><span className={s.out}>Sign out</span></div>
      </div>
    </div>
  </Frame>
); }
// P4. A rail of things to change; the one you tap opens beside the worm.
function P4() { const [c, setC] = useState(0); const [open, setOpen] = useState("Colour"); return (
  <Frame><Bar title="Player"><span className={s.nameLine}><b>{ME.name}</b><Pencil /></span><ProfileLink /></Bar>
    <div className={s.p4}>
      <div className={s.rail}>
        {["Colour", ...SLOTS.map(([l]) => l)].map((l) => (
          <button type="button" key={l} className={open === l ? s.railOn : undefined} onClick={() => setOpen(l)}>
            {l === "Colour" ? <i style={{ background: COLORS[c] }} /> : l === "Flag" ? <i><img src={flagUrl(ME.flag)} alt="" /></i> : l === "Head" ? <i className={s.gold}><Crown /></i> : <i className={s.plus}>+</i>}
            <small>{l}</small>
          </button>
        ))}
      </div>
      <div className={s.stage}><Worm color={COLORS[c]} id="w4" /></div>
      <div className={s.picker}><label>{open}</label>{open === "Colour" ? <Swatches pick={c} setPick={setC} col /> : <div className={s.pickGrid}>{[0, 1, 2, 3, 4, 5].map((k) => <span key={k} className={k === 0 ? s.pickOn : undefined} />)}</div>}</div>
    </div>
  </Frame>
); }

// ---------------- Profile ----------------
const Me = ({ small }: { small?: boolean }) => (
  <span className={`${s.me} ${small ? s.meSmall : ""}`}><img src={flagUrl(ME.flag)} alt="" /><span><b>{ME.name}</b><small><Pencil />Player settings</small></span></span>
);
const Rank = ({ r, row }: { r: (typeof RANKS)[number]; row?: boolean }) => (
  <div className={`${s.rank} ${row ? s.rankRow : ""}`} style={{ ["--c" as any]: (r.look as any).color }}>
    <RankBadge look={r.look} className={s.badge} />
    <span className={s.rankTxt}><b>{r.mode}</b><em>{r.tier}</em></span><strong>{r.rating}</strong>
    <i className={s.rankBar}><u style={{ width: `${r.pct}%` }} /></i>
  </div>
);
const Royale = ({ compact }: { compact?: boolean }) => (
  <div className={`${s.roy} ${compact ? s.royCompact : ""}`}>
    <span className={s.royHead}><i><Crown /></i><span><small>Royale wins</small><b>7</b></span><em>Played 31 · Best 1st</em></span>
    <span className={s.hexes}>{REWARDS.map(([wins, eggs, st]) => <span key={wins} className={`${s.hex} ${s[st]}`}><i>{st === "locked" ? wins : st === "ready" ? "Open" : "✓"}</i><small>{eggs} eggs</small></span>)}</span>
  </div>
);
const TABS = ["Overview", "Achievements", "Matches"];
const AchList = () => <div className={s.achs}>{ACHS.map(([n, v, g]) => <span key={n} className={s.ach}><b>{n}</b><i><u style={{ width: `${(v / g) * 100}%` }} /></i><small>{v}/{g}</small></span>)}</div>;
const MatchList = () => <div className={s.matches}>{MATCHES.map(([m, r, t]) => <span key={m + t} className={s.match}><b>{m}</b><span>{r}</span><small>{t}</small></span>)}</div>;
const Body = ({ tab }: { tab: string }) => tab === "Achievements" ? <AchList /> : tab === "Matches" ? <MatchList /> : null;

// R1. Tabs in the top bar (as now). You on the left, ranks and the Royale beside.
function R1() { const [tab, setTab] = useState("Overview"); return (
  <Frame><Bar title="Profile"><span className={s.tabs}>{TABS.map((t) => <button type="button" key={t} className={t === tab ? s.tabOn : undefined} onClick={() => setTab(t)}>{t}</button>)}</span></Bar>
    {tab === "Overview" ? <div className={s.r1}><div className={s.r1Left}><Me /><Royale compact /></div><div className={s.r1Right}>{RANKS.map((r) => <Rank key={r.mode} r={r} row />)}</div></div> : <div className={s.pad}><Body tab={tab} /></div>}
  </Frame>
); }
// R2. A rail down the left: you on top of it, the three pages under.
function R2() { const [tab, setTab] = useState("Overview"); return (
  <Frame><Bar title="Profile" />
    <div className={s.r2}>
      <div className={s.r2Rail}><Me small />{TABS.map((t) => <button type="button" key={t} className={t === tab ? s.tabOn : undefined} onClick={() => setTab(t)}>{t}</button>)}</div>
      <div className={s.r2Main}>{tab === "Overview" ? <><div className={s.ranks2}>{RANKS.map((r) => <Rank key={r.mode} r={r} />)}</div><Royale /></> : <Body tab={tab} />}</div>
    </div>
  </Frame>
); }
// R3. One long page, no tabs: ranks and the Royale on the first screen, the rest below.
function R3() { return (
  <Frame tall><Bar title="Profile"><Me small /></Bar>
    <div className={s.r3}>
      <div className={s.ranks2}>{RANKS.map((r) => <Rank key={r.mode} r={r} />)}</div>
      <Royale />
      <h4>Achievements</h4><AchList />
      <h4>Matches</h4><MatchList />
    </div>
  </Frame>
); }
// R4. Tabs at the bottom, like an app: the top bar keeps you and the eggs.
function R4() { const [tab, setTab] = useState("Overview"); return (
  <Frame><Bar title="Profile"><Me small /></Bar>
    <div className={s.r4}>{tab === "Overview" ? <><div className={s.r4Ranks}>{RANKS.map((r) => <Rank key={r.mode} r={r} />)}</div><Royale /></> : <Body tab={tab} />}</div>
    <div className={s.bottomTabs}>{TABS.map((t) => <button type="button" key={t} className={t === tab ? s.tabOn : undefined} onClick={() => setTab(t)}>{t}</button>)}</div>
  </Frame>
); }

const PLAYER: [string, () => React.ReactElement][] = [
  ["P1. The worm in the middle: name and colour left, what you wear right", P1],
  ["P2. The worm is the page: the controls sit over its arena", P2],
  ["P3. Two halves: the worm and your name, the choices beside it", P3],
  ["P4. A rail of things to change, the picker opens beside the worm", P4],
];
const PROFILE: [string, () => React.ReactElement][] = [
  ["R1. Tabs in the top bar", R1],
  ["R2. A rail down the left side", R2],
  ["R3. One long page, no tabs", R3],
  ["R4. Tabs along the bottom", R4],
];

export default function PhonePlayerSheet() {
  return (
    <div className={s.page}>
      <h1>Player settings (landscape phone, full screen)</h1>
      {PLAYER.map(([t, D]) => <section key={t}><h2>{t}</h2><D /></section>)}
      <h1>Profile (landscape phone)</h1>
      {PROFILE.map(([t, D]) => <section key={t}><h2>{t}</h2><D /></section>)}
    </div>
  );
}
